import { file2Base64, HttpError } from '@pankod/refine-core';
import {
  Avatar,
  Col,
  Edit,
  Form,
  getValueFromEvent,
  Input,
  ListButton,
  Row,
  useForm,
  Select,
  Space,
  Upload,
  notification,
} from '@pankod/refine-antd';
import { IRole, IUser, IUserVariable } from 'interfaces';
import {
  beforeImageUploadValidation,
  isJpgOrPngFile,
  isLessThanSize,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { useState, useEffect } from 'react';
import { getRoles } from 'services/role';

export const UserEdit: React.FC = () => {
  const [roles, setRoles] = useState<IRole[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { formProps, saveButtonProps, queryResult } = useForm<IUser, HttpError, IUserVariable>({
    metaData: {
      fields: [
        'id',
        'name',
        'email',
        'userCode',
        'mobileNumber',
        'role',
        {
          userPhoto: [
            {
              avatar: ['url', 'thumbUrl'],
            },
          ],
        },
      ],
    },
  });

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const roles = await getRoles();
        setRoles(roles);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsLoading(false);
    })();
  }, []);

  const userPhoto = queryResult?.data?.data.userPhoto;
  let avatarURL;
  if (userPhoto instanceof Array) {
    avatarURL = userPhoto[userPhoto?.length - 1]?.avatar?.url;
  }

  return (
    <Edit
      isLoading={queryResult?.isFetching || isLoading}
      saveButtonProps={saveButtonProps}
      headerProps={{ extra: <ListButton /> }}
    >
      <Form
        {...formProps}
        layout="vertical"
        initialValues={{
          isActive: true,
          ...formProps.initialValues,
        }}
        onFinish={async (values) => {
          const base64Files = [];
          // @ts-ignore
          let { avatar } = values;

          if (avatar && !Array.isArray(avatar)) {
            base64Files.push(avatar);
          }

          if (avatar && avatar.length) {
            const file = avatar[0];
            const format = file.type || '';
            const size = file.size || 0;

            if (
              isJpgOrPngFile(format, file.name || '') &&
              isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)
            ) {
              if (file.originFileObj) {
                const base64String = await file2Base64(file);
                const { originFileObj, ...rest } = file;
                base64Files.push({ ...rest, base64String });
              } else {
                base64Files.push(file);
              }
            }
          }

          return (
            formProps.onFinish &&
            formProps.onFinish({
              ...values,
              // @ts-ignore
              avatar: base64Files,
            })
          );
        }}
      >
        <Row gutter={[56, 0]} wrap>
          <Col xs={24} lg={8}>
            <Space
              direction="vertical"
              style={{
                width: '100%',
                textAlign: 'center',
                marginBottom: '16px',
                marginTop: '6px',
              }}
            >
              <Form.Item
                name="avatar"
                valuePropName="file"
                getValueFromEvent={getValueFromEvent}
                noStyle
              >
                <Upload.Dragger
                  listType="picture"
                  accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
                  maxCount={1}
                  beforeUpload={beforeImageUploadValidation}
                >
                  <Avatar
                    size={200}
                    style={{ objectFit: 'cover' }}
                    src={avatarURL || defaultAvatar}
                    alt="Store Location"
                  />
                  <p className="ant-upload-text">Drag & drop an image file in this area</p>
                </Upload.Dragger>
              </Form.Item>
            </Space>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item label="User Name" name="name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  required: true,
                  type: 'email',
                },
              ]}
            >
              <Input disabled />
            </Form.Item>
            <Form.Item label="Unique ID" name="userCode" rules={[{ required: true }]}>
              <Input disabled />
            </Form.Item>
            <Form.Item
              label="Mobile Number"
              name="mobileNumber"
              rules={[
                { required: true },
                {
                  pattern: /^(\+\d{1,3}[- ]?)?\d{8,12}$/,
                  message: 'Provide valid number',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="User Type" name="role" rules={[{ required: true }]}>
              <Select showSearch filterOption={false}>
                {roles.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
